import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, Calendar, User, ArrowRight, Tag } from 'lucide-react';

interface Article {
  _id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category: string;
  tags: string[];
  coverImage?: string;
  author: string;
  publishedAt?: string;
  createdAt: string;
}

const categoryLabels: Record<string, string> = {
  fundraising: 'גיוס תרומות',
  'donor-community': 'קהילת תורמים',
  management: 'ניהול עמותה',
  legal: 'משפטי',
  finance: 'כספים',
  other: 'כללי',
};

const categoryColors: Record<string, string> = {
  fundraising: 'bg-rose-100 text-rose-700',
  'donor-community': 'bg-violet-100 text-violet-700',
  management: 'bg-blue-100 text-blue-700',
  legal: 'bg-amber-100 text-amber-700',
  finance: 'bg-green-100 text-green-700',
  other: 'bg-gray-100 text-gray-700',
};

const ArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`https://amutech.co.il/api/articles/${slug}`);
        if (res.status === 404) {
          setError('המאמר לא נמצא');
          return;
        }
        if (!res.ok) throw new Error('שגיאה בטעינת המאמר');
        const data = await res.json();
        setArticle(data);
      } catch (e: any) {
        setError(e.message || 'שגיאה בטעינת המאמר');
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchArticle();
  }, [slug]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Render content with basic line breaks
  const renderContent = (content: string) => {
    return content.split('\n').map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < content.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  return (
    <div dir="rtl" className="min-h-screen bg-[#FAFAFA]">
      {/* Back button nav */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <button
            onClick={() => navigate('/knowledge')}
            className="flex items-center gap-2 text-sm text-[#1565C0] hover:text-blue-800 font-medium transition-colors"
          >
            <ArrowRight size={16} />
            חזרה למרכז הידע
          </button>
        </div>
      </div>

      {loading && (
        <div className="max-w-3xl mx-auto px-4 py-16 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-6" />
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-4" />
          <div className="h-5 bg-gray-200 rounded w-1/2 mb-10" />
          <div className="h-64 bg-gray-200 rounded-2xl mb-10" />
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-4 bg-gray-200 rounded mb-3" />
          ))}
        </div>
      )}

      {error && (
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <BookOpen size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-xl font-semibold text-gray-700 mb-2">{error}</p>
          <button
            onClick={() => navigate('/knowledge')}
            className="mt-4 px-6 py-2 bg-[#1565C0] text-white rounded-xl hover:bg-blue-700 transition"
          >
            חזרה למרכז הידע
          </button>
        </div>
      )}

      {!loading && !error && article && (
        <article className="max-w-3xl mx-auto px-4 py-10">
          {/* Category + meta */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${categoryColors[article.category] || categoryColors.other}`}>
              {categoryLabels[article.category] || 'כללי'}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4 leading-tight">
            {article.title}
          </h1>

          {/* Excerpt */}
          {article.excerpt && (
            <p className="text-lg text-gray-500 mb-6 leading-relaxed border-r-4 border-[#1565C0] pr-4">
              {article.excerpt}
            </p>
          )}

          {/* Meta: author + date */}
          <div className="flex items-center gap-4 text-sm text-gray-400 mb-8 pb-6 border-b border-gray-200">
            <span className="flex items-center gap-1.5">
              <User size={14} />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar size={14} />
              {formatDate(article.publishedAt || article.createdAt)}
            </span>
          </div>

          {/* Cover image */}
          {article.coverImage && (
            <div className="rounded-2xl overflow-hidden mb-8 shadow-md">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-48 md:h-72 object-cover max-w-full"
              />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-lg max-w-none text-gray-700 leading-loose text-base">
            <div className="whitespace-pre-wrap">
              {renderContent(article.content)}
            </div>
          </div>

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="mt-10 pt-6 border-t border-gray-200">
              <div className="flex flex-wrap items-center gap-2">
                <Tag size={14} className="text-gray-400" />
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Back to knowledge */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={() => navigate('/knowledge')}
              className="flex items-center gap-2 px-6 py-3 bg-[#1565C0] text-white rounded-xl hover:bg-blue-700 transition font-medium"
            >
              <ArrowRight size={16} />
              חזרה למרכז הידע
            </button>
          </div>
        </article>
      )}
    </div>
  );
};

export default ArticlePage;
